// src/store/lookupsSlice.js
import { createSlice } from "@reduxjs/toolkit";
import { fetchDashboardStats , fetchDashboardLastSevenMonthSalesAndPurchase } from "../commonThunks/dashboard";
import {
  fetchSingleInstitutionDetails,
  fetchEmployees,
  fetchCustomers,
  fetchSuppliers,
  fetchPreviousDue,
  fetchItems,
  fetchGroups,
  fetchCategories,
  fetchUsers,
  fetchTagGroups,
  fetchTagGroupFields,
  fetchLocations,
  fetchOrigins,
  fetchItemModels,
  fetchItemRack,
  fetchItemUnit,
  fetchBranchList,
  fetchWarehouse,
  fetchTaxAccs,
  fetchDiscountAccs,
  fetchBatchAvailability,
  fetchLoadIntoAccs,
  fetchLoadIntoAccsExcludeCheque,
  fetchSalaryAccs,
  fetchCurrentAssetAccs,
  fetchFixedAssetAccs,
  fetchAccountsByType,
  fetchUserRoles,
  fetchTagsOnEdit,
  fetchCurrentEmployee,
   fetchGenericNames
} from "../commonThunks/lookups";

const initialState = {  
  institutionData: {},
  employees: [],
  currentEmployee:{},
  customers: [],
  suppliers: [],
  items: [],
  groups: [],
  categories: [],
  users: [],
  tagGroups: [],
  tagFields: [],
  editTags: [],
  locations: [],
  origins: [],
  itemUnit: [],
  itemRacks: [],
  itemModels: [],
  branches: [],
  batchAvailability: {},
  warehouses: [],
  taxAccs: [],
  curAssAccs: [],
  fixedAssetAccs: [],
  discountAccs: [],
    genericNames: [], 
  salaryAccs: [],
  loadIntoAccs: [],
  loadIntoAccounts:[],
  accountsByType: [],
  previousDue: 0,
  userRoles: [],
  dashboardStats: {
    sales: 0,
    grossProfit: 0,
    netProfit: 0,
    activeUsers: 0,
    cashBalance: 0,
    bankBalance: 0,
    receivables: 0,
    payables: 0,
    recentTransactions: [],
    currency: "",
  },

  dashboardLastSevenMonthSalesAndPurchase: [],

  // ✅ status tracking
  loading: {
    institutionData: false,
    employees: false,
    currentEmployee:false,
    customers: false,
    suppliers: false,
    previousDue: false,
    items: false,
    groups: false,
    categories: false,
    users: false,
    tagGroups: false,
    tagFields: false,
    editTags: false,
    locations: false,
    origins: false,
    itemUnit: false,
    itemRacks: false,
    itemModels: false,
    branches: false,
    batchAvailability: false,
    warehouses: false,
    taxAccs: false,
    curAssAccs: false,
    fixedAssetAccs: false,
    discountAccs: false,
    salaryAccs: false,
    loadIntoAccs: false,
    loadIntoAccounts:false,
      genericNames: false, 
    dashboardStats: false,
    accountsByType: false,
    userRoles: false,
    dashboardLastSevenMonthSalesAndPurchase:false
  },
  errors: {
    institutionData: null,
    employees: null,
    currentEmployee:false,
    customers: null,
    suppliers: null,
    previousDue: null,
    items: null,
    groups: null,
    categories: null,
    users: null,
    tagGroups: null,
    tagFields: null,
    editTags: null,
    locations: null,
    origins: null,
    itemUnit: null,
    itemRacks: null,
    itemModels: null,
    branches: null,
    batchAvailability: false,
    warehouses: null,
    taxAccs: null,
      genericNames: null, 
    curAssAccs: null,
    fixedAssetAccs: null,
    discountAccs: null,
    salaryAccs: null,
    loadIntoAccs: null,
    loadIntoAccounts:null,
    dashboardStats: null,
    accountsByType: null,
    userRoles: null,
    dashboardLastSevenMonthSalesAndPurchase:null,
  },
};

const lookupsSlice = createSlice({
  name: "lookups",
  initialState,
  reducers: {
    resetLookups: () => initialState,
    saveDraftForReturn: (state, action) => {
      const { data, returnTarget } = action.payload;
      state.draftData = data;
      state.returnTarget = returnTarget; // e.g., "clientUserModal"
      state.shouldReopen = true;
    },
    clearDraftAfterReturn: (state) => {
      state.draftData = null;
      state.returnTarget = null;
      state.shouldReopen = false;
    },
  },
  extraReducers: (builder) => {
    const addCases = (thunk, key) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.loading[key] = true;
          state.errors[key] = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state.loading[key] = false;
          state[key] = action.payload;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.loading[key] = false;
          state.errors[key] = action.payload || "Something went wrong";
        });
    };

    addCases(fetchSingleInstitutionDetails, "institutionData");
    addCases(fetchEmployees, "employees");
    addCases(fetchCurrentEmployee, "currentEmployee");
    addCases(fetchCustomers, "customers");
    addCases(fetchSuppliers, "suppliers");
    addCases(fetchPreviousDue, "previousDue");
    addCases(fetchItems, "items");
    addCases(fetchTaxAccs, "taxAccs");
    addCases(fetchCurrentAssetAccs, "curAssAccs");
    addCases(fetchFixedAssetAccs, "fixedAssetAccs");
    addCases(fetchDiscountAccs, "discountAccs");
    addCases(fetchSalaryAccs, "salaryAccs");
    addCases(fetchLoadIntoAccs, "loadIntoAccs");
    addCases(fetchLoadIntoAccsExcludeCheque, "loadIntoAccounts");
    addCases(fetchGroups, "groups");
    addCases(fetchCategories, "categories");
    addCases(fetchUsers, "users");
    addCases(fetchTagGroups, "tagGroups");
    addCases(fetchTagGroupFields, "tagFields");
    addCases(fetchLocations, "locations");
    addCases(fetchItemModels, "itemModels");
    addCases(fetchItemRack, "itemRacks");
    addCases(fetchItemUnit, "itemUnit");
    addCases(fetchOrigins, "origins");
    addCases(fetchGenericNames, "genericNames");
    addCases(fetchBranchList, "branches");
    addCases(fetchWarehouse, "warehouses");
    addCases(fetchDashboardStats, "dashboardStats");
    addCases(fetchDashboardLastSevenMonthSalesAndPurchase, "dashboardLastSevenMonthSalesAndPurchase");
    addCases(fetchAccountsByType, "accountsByType");
    addCases(fetchUserRoles, "userRoles");
    addCases(fetchTagsOnEdit, "editTags");

    // ✅ Special handling for batch availability
    builder
      .addCase(fetchBatchAvailability.pending, (state) => {
        state.loading.batchAvailability = true;
        state.errors.batchAvailability = null;
      })
      .addCase(fetchBatchAvailability.fulfilled, (state, action) => {
        state.loading.batchAvailability = false;
        const { item_id, batches } = action.payload;
        state.batchAvailability[item_id] = batches;
      })
      .addCase(fetchBatchAvailability.rejected, (state, action) => {
        state.loading.batchAvailability = false;
        state.errors.batchAvailability =
          action.payload || "Something went wrong";
      });
  },
});

export const { resetLookups, saveDraftForReturn, clearDraftAfterReturn } = lookupsSlice.actions;

export default lookupsSlice.reducer;
